import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Chip,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

const CancelledPlotRefund = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [bookingId, setBookingId] = useState('');
  const [showRefundDetails, setShowRefundDetails] = useState(false);
  const [refundData, setRefundData] = useState([]);

  // Sample refund data that will be loaded after search
  const sampleRefundData = [
    {
      id: 1,
      bookingId: 'BKG001',
      customerName: 'Rajesh Kumar',
      plotNo: 'A-101',
      site: 'Site A',
      sector: 'Sector 5',
      block: 'Block A',
      totalPaidAmount: 500000,
      cancellationCharges: 50000,
      deductionAmount: 25000,
      refundableAmount: 425000,
      cancellationDate: '2024-10-01',
      refundStatus: 'Pending',
      refundDate: '',
      refundMode: '',
      remarks: 'Financial constraints',
    },
    {
      id: 2,
      bookingId: 'BKG002',
      customerName: 'Priya Sharma',
      plotNo: 'B-205',
      site: 'Site B',
      sector: 'Sector 3',
      block: 'Block B',
      totalPaidAmount: 750000,
      cancellationCharges: 75000,
      deductionAmount: 35000,
      refundableAmount: 640000,
      cancellationDate: '2024-09-28',
      refundStatus: 'Processed',
      refundDate: '2024-10-05',
      refundMode: 'NEFT',
      remarks: 'Job relocation',
    },
    {
      id: 3,
      bookingId: 'BKG003',
      customerName: 'Amit Patel',
      plotNo: 'C-150',
      site: 'Site C',
      sector: 'Sector 7',
      block: 'Block C',
      totalPaidAmount: 600000,
      cancellationCharges: 60000,
      deductionAmount: 30000,
      refundableAmount: 510000,
      cancellationDate: '2024-09-25',
      refundStatus: 'Approved',
      refundDate: '',
      refundMode: '',
      remarks: 'Medical emergency',
    },
  ];

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleSearch = () => {
    if (bookingId.trim()) {
      // Filter data based on booking ID (in real app, this would be an API call)
      const filteredData = sampleRefundData.filter(item => 
        item.bookingId.toLowerCase().includes(bookingId.toLowerCase())
      );
      setRefundData(filteredData);
      setShowRefundDetails(true);
    } else {
      alert('Please enter a Booking ID');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Processed':
        return 'success';
      case 'Approved':
        return 'info';
      case 'Pending':
        return 'warning';
      case 'Rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3, textAlign: 'center' }}>
        Cancel Booking Refund
      </Typography>

      {/* Search Section */}
      <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
          Search Booking for Refund
        </Typography>
        
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="Booking ID"
              value={bookingId}
              onChange={(e) => setBookingId(e.target.value)}
              size="small"
              placeholder="Enter Booking ID to search for refund details"
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              size="small"
              sx={{ py: 0.5, fontSize: '0.8rem', width: 'auto', minWidth: '100px' }}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Refund Details Table */}
      {showRefundDetails && (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3 }}>
            Refund Details for Booking ID: {bookingId}
          </Typography>

          {refundData.length === 0 ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              No refund records found for this booking ID.
            </Alert>
          ) : (
            <>
              <Typography variant="body2" color="primary" sx={{ mb: 3, fontWeight: 'bold' }}>
                Total Records: {refundData.length}
              </Typography>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>S.No</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Booking ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Customer Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Plot Details</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Total Paid Amount</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Cancellation Charges</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Deduction Amount</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Refundable Amount</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Cancellation Date</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Refund Status</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Refund Date</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Refund Mode</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Remarks</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {refundData
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((row, index) => (
                        <TableRow 
                          key={row.id}
                          sx={{ 
                            '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                            '&:hover': { backgroundColor: '#e3f2fd' }
                          }}
                        >
                          <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'primary.main' }}>
                              {row.bookingId}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {row.customerName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                              {row.plotNo}<br/>
                              {row.site}, {row.sector}<br/>
                              {row.block}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {formatCurrency(row.totalPaidAmount)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'error.main' }}>
                              {formatCurrency(row.cancellationCharges)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'warning.main' }}>
                              {formatCurrency(row.deductionAmount)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'success.main' }}>
                              {formatCurrency(row.refundableAmount)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {row.cancellationDate}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={row.refundStatus} 
                              size="small" 
                              color={getStatusColor(row.refundStatus)}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {row.refundDate || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {row.refundMode || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontSize: '0.85rem', maxWidth: '150px' }}>
                              {row.remarks}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={refundData.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                sx={{ mt: 2 }}
              />
            </>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default CancelledPlotRefund;